import { ComponentProps } from 'react';
import * as _ from 'lodash';
import { createRender, getObjKeys, getPath, getString, isWithinBreakpoint, renderHeader } from './list-table.helpers';
import {
  ColumnOptions,
  PropMap,
  TableConfig,
  PreparedData,
  ColumnCell,
  ReactComponent,
  ColumnGroupCell,
  TableCell,
  TableStyles,
  TableRowStyles,
  ComputedRowStyles,
  HeaderProp
} from './list-table.types';
import { Breakpoint } from '@mui/material/styles';

class InitTableWrapper<T extends object, Q extends object = T> {
  initTable(_e: T) {
    return initTable<T, Q>([]);
  }
}

export type InitializedTable<T extends object, Q extends object = T> = ReturnType<InitTableWrapper<T, Q>['initTable']>;

export function initTable<Data extends object, QueryData extends object = Data>(_data?: Data[]) {
  // eslint-disable-next-line prettier/prettier
  const columns: (ColumnCell<Data, ReactComponent, QueryData> | ColumnGroupCell<Data, ReactComponent, QueryData>)[] =
    [];
  let config: TableConfig<Data> = {};
  let styles: TableStyles = {};
  let rowStyles: TableRowStyles<Data> = {};

  function getProps<C extends ReactComponent>(row: Data, props: PropMap<Data, C>) {
    const loadedProps = getObjKeys(props).reduce((acc, key) => {
      const accessor = props[key];
      if (!accessor) return acc;
      if (typeof accessor === 'function') {
        const value = accessor(row);
        return {
          ...acc,
          [key]: value
        };
      }
      const path = getPath(accessor);
      if (typeof path === 'string') {
        const value = _.get(row, path);
        return {
          ...acc,
          [key]: value
        };
      }
      const string = getString(accessor);
      if (typeof string === 'string') {
        return {
          ...acc,
          [key]: string
        };
      }
      return acc;
    }, {} as ComponentProps<C>);

    return loadedProps;
  }

  function renderCell<C extends ReactComponent>(Component: C, props: PropMap<Data, C>) {
    return function doRender(row: Data) {
      const loadedProps = getProps(row, props);
      return createRender(Component, loadedProps);
    };
  }

  return {
    config(tableConfig: TableConfig<Data>) {
      config = {
        ...config,
        ...tableConfig
      };
    },

    styles(styleObj: TableStyles) {
      styles = {
        ...styles,
        ...styleObj
      };
    },

    rowStyles(styleObj: TableRowStyles<Data>) {
      rowStyles = {
        ...rowStyles,
        ...styleObj
      };
    },

    addPredicate(header: string, predicate: (...args: any) => boolean) {
      const column = columns.find((col) => col.header === header);
      if (column) column.options = { ...column.options, predicate };
    },

    prepareData: _.memoize((rows: Data[]): PreparedData<Data>[] => {
      return rows
        .filter((row, idx) => idx === rows.findIndex((r) => JSON.stringify(r) === JSON.stringify(row)))
        .map((row) => ({
          value: row,
          key: JSON.stringify(row)
        }));
    }),

    title(text: string) {
      config = {
        ...config,
        title: text
      };
    },

    controls<C extends ReactComponent>(Input: C, props: ComponentProps<C>) {
      config.controls = () => createRender(Input, props);
    },

    columnGroup(header: string) {
      const columnGroup: ColumnGroupCell<Data, ReactComponent, QueryData> = {
        id: `${columns.length}-${header}`,
        header,
        renderHeader: renderHeader(header),
        type: 'COLUMN_GROUP',
        rows: []
      };

      columns.push(columnGroup);

      function options(options: ColumnOptions<Data, QueryData>) {
        columnGroup.options = options;
      }

      function row<C extends ReactComponent>(
        Component: C,
        props: PropMap<Data, C>,
        visibilityPredicate?: () => boolean
      ) {
        // The visibility predicate is false, then we don't add the row to the column group
        if (visibilityPredicate && !visibilityPredicate()) {
          return {
            row,
            options
          };
        }

        const cell: TableCell<Data, C> = {
          id: `${header}:${columnGroup.rows.length}`,
          Component,
          props,
          renderCell: renderCell(Component, props)
        };

        columnGroup.rows.push(cell);

        return {
          row,
          options
        };
      }

      return {
        row,
        options
      };
    },
    column<C extends ReactComponent>(header: HeaderProp, Component: C, props: PropMap<Data, C>) {
      const column: ColumnCell<Data, C, QueryData> = {
        type: 'COLUMN',
        id: `${columns.length}:${header}`,
        header: typeof header === 'string' ? header : header.name,
        Component,
        renderHeader: renderHeader(header),
        props,
        renderCell: renderCell(Component, props)
      };

      columns.push(column);

      return {
        options(options: ColumnOptions<Data, QueryData>) {
          column.options = options;
        }
      };
    },

    getColumns(
      currentBreakpoint?: Breakpoint
    ): (ColumnCell<Data, ReactComponent, QueryData> | ColumnGroupCell<Data, ReactComponent, QueryData>)[] {
      if (!currentBreakpoint) return [];
      return columns.filter((column) => {
        if (column.options) {
          if (column.options.predicate && column.options.predicate() === false) {
            return false;
          }
          return isWithinBreakpoint(
            currentBreakpoint,
            column.options.maxBreakpoint,
            column.options.minBreakpoint,
            column.options.atBreakpoints
          );
        }
        return true;
      });
    },

    getConfig() {
      return config;
    },

    getStyles() {
      return styles;
    },

    getRowStyles(row: Data) {
      return Object.keys(rowStyles).reduce((acc, key) => {
        const tag = rowStyles[key as keyof typeof rowStyles];
        if (!tag) {
          return acc;
        }
        let properties: React.CSSProperties;
        if (typeof tag === 'function') {
          properties = tag(row) as React.CSSProperties;
        } else {
          properties = tag as React.CSSProperties;
        }
        return {
          ...acc,
          [key]: properties
        };
      }, {} as ComputedRowStyles);
    },

    includeTableHeader() {
      return config.title !== undefined;
    }
  };
}
